/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getAttributeValuesAsync, getByIdAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    buildCacheKey,
    generateImageUrl,
    getPriceForSelectedVariant,
    getProductUrlSync,
    getSelectedProductIdFromActionInput,
    PriceForSelectedVariantInput,
    removeDomainQspFromUrl
} from '@msdyn365-commerce-modules/retail-actions';

import { IPageSummaryData, IProductPageSummaryConfig } from '@msdyn365-commerce-modules/page-summary';

/** Product Page Summary Input */
export class ProductPageSummaryInput implements IActionInput {
    public apiSettings: ICommerceApiSettings;

    public config: IProductPageSummaryConfig;

    public productId: number;

    constructor(config: IProductPageSummaryConfig, apiSettings: ICommerceApiSettings, productId: number) {
        this.config = config || {};
        this.apiSettings = apiSettings;
        this.productId = productId;
    }

    public getCacheObjectType = (): string => 'ProductPageSummary';

    public getCacheKey = (): string => `${buildCacheKey('ProductPageSummary', this.apiSettings)}-${this.config.title}-${this.productId}`;

    public dataCacheType = (): CacheType => 'application';
}

export interface IProductPageSummary extends IPageSummaryData {
    price?: number;
    productId?: number;
    productBrand?: string;
}

const createInput = (args: ICreateActionContext) => {
    const productId = getSelectedProductIdFromActionInput(args);

    if (productId) {
        return new ProductPageSummaryInput(<IProductPageSummaryConfig>args.config, args.requestContext.apiSettings, +productId);
    }
    throw new Error('Unable to create ProductPageSummaryAction input, no productId found on module config or query');
};

// Checks product attributes for the presence of brand attribute to extract the brand name for product metadata
const getProductBrand = async (
    context: IActionContext,
    recordId: number,
    channelId: number,
    catalogId: number
): Promise<string | undefined> => {
    const productAttributes = await getAttributeValuesAsync(
        { callerContext: context, queryResultSettings: {} },
        recordId,
        channelId,
        catalogId
    );
    for (const productAttribute of productAttributes) {
        if (productAttribute.Name === 'Brand') {
            return productAttribute.TextValue;
        }
    }
    return;
};

const action = async (input: ProductPageSummaryInput, context: IActionContext): Promise<IProductPageSummary> => {
    const { config, productId, apiSettings } = input;
    let simpleProductData;
    let extraProductData;
    let productPrice;
    try {
        simpleProductData = await getByIdAsync({ callerContext: context }, productId, apiSettings.channelId);
        extraProductData = await getAttributeValuesAsync(
            { callerContext: context, queryResultSettings: {} },
            productId,
            context.requestContext.apiSettings.channelId,
            context.requestContext.apiSettings.catalogId
        );
        productPrice = await getPriceForSelectedVariant(
            new PriceForSelectedVariantInput(simpleProductData.RecordId, apiSettings.channelId),
            context
        );
    } catch {
        // Do nothing, if there's an error we fall back to values defined from config
    }

    if (simpleProductData) {
        let productUrl: string | undefined;
        try {
            productUrl = getProductUrlSync(simpleProductData, context);
            const canonicalDomain = context.requestContext.canonicalDomain;
            if (productUrl && canonicalDomain) {
                productUrl = `https://${canonicalDomain}${productUrl}`.toLocaleLowerCase();
                productUrl = removeDomainQspFromUrl(productUrl, context.requestContext);
            } else {
                productUrl = undefined;
            }
        } catch {
            productUrl = undefined;
        }
        let productBrand: string | undefined;
        try {
            productBrand = await getProductBrand(context, productId, apiSettings.channelId, apiSettings.catalogId);
        } catch {
            productBrand = undefined;
        }
        const customMetaDesc = extraProductData?.filter(product => product.Name === 'Meta description')[0]?.TextValue;
        const productDescription = extraProductData?.filter(product => product.Name === 'Product description')[0]?.TextValue;
        const customDesc = customMetaDesc === null ? productDescription : customMetaDesc;

        const customTitle = extraProductData?.filter(product => product.Name === 'Meta title')[0]?.TextValue;

        return {
            title: customTitle !== null && customTitle !== undefined ? customTitle : simpleProductData.Name,
            description: customDesc !== null ? customDesc : simpleProductData.Description,
            sharingImageUrl: generateImageUrl(simpleProductData.PrimaryImageUrl, apiSettings),
            canonicalUrl: productUrl,
            faviconUrl: config && config.faviconUrl,
            productId: Number(simpleProductData.ItemId),
            price: productPrice ? productPrice.CustomerContextualPrice : simpleProductData.AdjustedPrice,
            productBrand
        };

        // If the action fails fallback to values defined from config
    } else if (config) {
        return {
            title: config.title,
            description: config.description,
            sharingImageUrl: config.sharingImage && config.sharingImage.src,
            faviconUrl: config && config.faviconUrl
        };
    }
    return {};
};

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/page-summary/product-page-summary',
    action: <IAction<IProductPageSummary>>action,
    input: createInput
});
